import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import Icon from '../../../components/AppImage';
import Image from '../../../components/AppImage';


const CareersTab = ({ institution }) => {
  const COLORS = ['#1E40AF', '#059669', '#F59E0B', '#EF4444', '#8B5CF6'];

  return (
    <div className="space-y-6">
      {/* Employment Statistics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
            Taux d'emploi par secteur
          </h3>
          <div className="h-64" aria-label="Graphique du taux d'emploi par secteur">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={institution?.employmentBySector}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100)?.toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {institution?.employmentBySector?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
            Salaires moyens par expérience
          </h3>
          <div className="h-64" aria-label="Graphique des salaires moyens">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={institution?.salaryByExperience}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="experience" />
                <YAxis />
                <Tooltip 
                  formatter={(value) => [`${value} MAD`, 'Salaire moyen']}
                />
                <Bar dataKey="salary" fill="var(--color-secondary)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      {/* Key Employment Metrics */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
          Indicateurs clés d'employabilité
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {institution?.employmentMetrics?.map((metric, index) => (
            <div key={index} className="text-center p-4 bg-muted rounded-lg">
              <div className="text-2xl font-bold text-primary mb-1">{metric?.value}</div>
              <div className="text-sm text-muted-foreground">{metric?.label}</div>
            </div>
          ))}
        </div>
      </div>
      {/* Top Employers */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
          Principaux employeurs de nos diplômés
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {institution?.topEmployers?.map((employer, index) => (
            <div key={index} className="flex items-center gap-3 p-4 border border-border rounded-lg">
              <div className="w-12 h-12 bg-muted rounded-lg overflow-hidden">
                <Image 
                  src={employer?.logo} 
                  alt={employer?.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <div className="font-medium text-foreground text-sm">{employer?.name}</div>
                <div className="text-xs text-muted-foreground">{employer?.hiredCount} diplômés</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Career Paths */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
          Parcours professionnels typiques
        </h3>
        <div className="space-y-4">
          {institution?.careerPaths?.map((path, index) => (
            <div key={index} className="border border-border rounded-lg p-4">
              <h4 className="font-medium text-foreground mb-3">{path?.title}</h4>
              <div className="flex flex-wrap gap-2 mb-3">
                {path?.positions?.map((position, posIndex) => (
                  <div key={posIndex} className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-full">
                      {position}
                    </span>
                    {posIndex < path?.positions?.length - 1 && (
                      <Icon name="ArrowRight" size={16} className="text-muted-foreground" />
                    )}
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Icon name="Clock" size={14} />
                  <span>Progression: {path?.timeframe}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Icon name="TrendingUp" size={14} />
                  <span>Salaire final: {path?.finalSalary} MAD</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Alumni Success Stories */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
          Témoignages d'anciens étudiants
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {institution?.alumniStories?.map((story, index) => (
            <div key={index} className="border border-border rounded-lg p-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-medium">
                  {story?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                </div>
                <div>
                  <h4 className="font-medium text-foreground">{story?.name}</h4>
                  <p className="text-sm text-muted-foreground">{story?.position}</p>
                  <p className="text-sm text-muted-foreground">{story?.company}</p>
                </div>
              </div>
              <blockquote className="text-sm text-muted-foreground italic">
                "{story?.testimonial}"
              </blockquote>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CareersTab;